import ClusterPage, { H2, P, Stat } from '../../../components/cluster/ClusterPage';
import { VERTICALS, CLUSTER_CITIES, FACTS } from '../../../data/cluster';

export default function HubPage() {
  return (
    <ClusterPage
      slug="chatgpt-ads-south-florida-home-services"
      keyword="chatgpt ads for south florida home services"
      title="ChatGPT Ads for South Florida Home Services"
      h1="ChatGPT Ads for South Florida Home Services"
      eyebrow="The land-grab window is open"
      leadAnswer="ChatGPT Ads let South Florida home-service contractors place a sponsored card beneath ChatGPT answers, targeted by ZIP code with a $3–5 cost-per-click and no minimum spend. FAME sets up the verified account, writes context-hint targeting around your highest-margin jobs, and wires conversion tracking so you own the AI answer before competitors notice."
      metaDescription="ChatGPT Ads for South Florida home services: ZIP-targeted sponsored cards in ChatGPT for roofers, HVAC, plumbers, electricians, garage door and restoration companies in Miami, Fort Lauderdale and West Palm Beach. Done-for-you by FAME."
      keywords="chatgpt ads for south florida home services, chatgpt ads contractors florida, openai ads agency florida, ai marketing south florida"
      areaServed={['Miami', 'Fort Lauderdale', 'West Palm Beach', 'South Florida']}
      serviceName="ChatGPT Ads Management for South Florida Home Services"
      serviceDescription="Done-for-you ChatGPT Ads setup, ZIP-targeted campaigns, context-hint copywriting, conversion tracking and GEO for Florida home-service contractors."
      faqs={[
        { q: 'What are ChatGPT Ads?', a: 'ChatGPT Ads are sponsored cards that appear below a ChatGPT answer, matched to the live conversation topic and clearly labeled "Sponsored." ' + FACTS.audience + ' ' + FACTS.trust },
        { q: 'Can a local Florida contractor actually run ChatGPT Ads now?', a: 'Yes. ' + FACTS.selfServe + ' ' + FACTS.noMinimum + ' ' + FACTS.geo + ' "Local services" is an explicitly eligible category, which covers roofing, HVAC, plumbing, electrical, garage door and restoration.' },
        { q: 'How much do ChatGPT Ads cost?', a: FACTS.cpc + ' You set daily and lifetime budgets per campaign, so a contractor can start with a few hundred dollars per week. See our ChatGPT Ads cost guide for the full breakdown.' },
        { q: 'Is this paid placement or getting recommended organically?', a: 'Both, and we sell both. Paid is the sponsored card below the answer. Organic (GEO) is getting your business named inside the answer itself. A contractor who does both owns the entire AI answer.' },
        { q: 'How does FAME charge for this?', a: 'A monthly management retainer: campaign optimization, context-hint iteration, reporting and conversion-tracking maintenance. We can bundle GEO into an "Own the ChatGPT Answer" package. Month-to-month, no long lock-ins.' },
      ]}
      breadcrumb={[
        { name: 'Home', url: 'https://floridaaimarketingexperts.com/' },
        { name: 'ChatGPT Ads for South Florida Home Services', url: 'https://floridaaimarketingexperts.com/chatgpt-ads-south-florida-home-services' },
      ]}
      upLinks={[
        { label: 'How much do ChatGPT Ads cost in 2026?', to: '/how-much-do-chatgpt-ads-cost' },
        { label: 'How to set up ChatGPT Ads (ads.openai.com)', to: '/how-to-set-up-chatgpt-ads' },
        { label: 'Get my business recommended by ChatGPT (GEO)', to: '/get-my-business-recommended-by-chatgpt' },
        { label: 'AI marketing for Florida contractors', to: '/ai-marketing-florida-contractors' },
      ]}
      crossLinks={[
        ...VERTICALS.map((v) => ({ label: `ChatGPT Ads for ${v.name}`, to: `/chatgpt-ads-for-${v.slug}-florida` })),
        ...CLUSTER_CITIES.map((c) => ({ label: `ChatGPT Ads in ${c.name}`, to: `/chatgpt-ads-${c.slug}` })),
      ]}
    >
      <H2>Why ChatGPT Ads matter for Florida home services right now</H2>
      <P>
        Your customers are already asking ChatGPT "who is the best roofer near me" and "should I repair
        or replace my AC." Right now most of your competitors are not on that surface. ChatGPT Ads is the
        first time a local contractor can buy placement directly inside that conversation.
      </P>
      <Stat>{FACTS.selfServe} {FACTS.noMinimum}</Stat>
      <P>
        Until late May 2026 the platform only offered country-level targeting, which made it useless for a
        single-market business. That changed days before this page was built.
      </P>
      <Stat>{FACTS.geo}</Stat>

      <H2>How ChatGPT Ads work (the 30-second version)</H2>
      <P>
        Ads are paid cards shown below a relevant ChatGPT answer, separate from the answer itself. You do
        not bid on keywords — you write plain-language context hints describing the conversations where you
        want to appear.
      </P>
      <Stat>{FACTS.hints} Instead of "ac repair miami" you describe the moment: a homeowner with a broken AC asking about emergency service.</Stat>
      <P>
        The account structure mirrors Google Ads: Campaign (objective, budget, ZIP targeting) → Ad Group
        (theme + context hints) → Ad (headline, description, image, landing page). For lead generation we
        use the Clicks objective and CPC bidding.
      </P>
      <Stat>{FACTS.cpc}</Stat>

      <H2>The six home-service verticals we run</H2>
      <P>
        We build campaigns for the six South Florida trades where a single booked job pays for months of
        ad spend: roofing, HVAC, plumbing, electrical, garage door, and fire/water/mold restoration. Each
        vertical gets context hints written around its highest-margin emergency job.
      </P>

      <H2>Paid + organic = owning the answer</H2>
      <P>
        Running a sponsored card is half the play. The other half is GEO — Generative Engine Optimization —
        which earns your business a mention inside the answer ChatGPT writes. The two surfaces are separate,
        and you can own both.
      </P>
      <Stat>{FACTS.conversionRate} {FACTS.geoLift}</Stat>

      <H2>The honest part: attribution</H2>
      <P>
        We lead with the truth most agencies hide. {FACTS.attribution} We instrument phone-click, form-submit
        and chat-open events through Google Tag Manager and UTM-tag every ad URL, so the first 30 days are a
        measured, directional test — not phantom ROAS.
      </P>
    </ClusterPage>
  );
}
